import React, { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../../db/firebase";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useTranslation } from "react-i18next";
import { HiOutlineUsers } from "react-icons/hi2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

interface UserEventsChartProps {
  organizationId: string | null;
}

interface UserStat {
  id: string;
  name: string;
  count: number;
}

const MAX_USERS = 12;

const UserEventsChart: React.FC<UserEventsChartProps> = ({
  organizationId,
}) => {
  const { t } = useTranslation();
  const [userStats, setUserStats] = useState<UserStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const fetchEventAssignments = async () => {
      if (!organizationId) {
        setUserStats([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        // All org events (Active + Archived)
        const eventsSnapshot = await getDocs(
          query(
            collection(db, "events"),
            where("organizationId", "==", organizationId),
          ),
        );

        const userEventCount: Record<string, number> = {};

        eventsSnapshot.forEach((eventDoc) => {
          const data = eventDoc.data();
          const leaders: string[] = Array.isArray(data.leaders)
            ? data.leaders.filter((id: unknown) => typeof id === "string")
            : [];
          const members: string[] = Array.isArray(data.members)
            ? data.members.filter((id: unknown) => typeof id === "string")
            : [];
          // Count once per event even if listed as both leader and member
          const uniqueUsers = new Set([...leaders, ...members]);
          uniqueUsers.forEach((userId) => {
            userEventCount[userId] = (userEventCount[userId] || 0) + 1;
          });
        });

        const userIds = Object.keys(userEventCount);
        const details = await Promise.all(
          userIds.map(async (userId) => {
            try {
              const userDoc = await getDoc(doc(db, "users", userId));
              const userData = userDoc.exists() ? userDoc.data() : null;
              const first = (userData?.firstName || "").trim();
              const last = (userData?.lastName || "").trim();
              const fullName = [first, last].filter(Boolean).join(" ");
              return {
                id: userId,
                name: fullName || userData?.email || t("unknownUser") || "Unknown",
                count: userEventCount[userId],
              } satisfies UserStat;
            } catch {
              return {
                id: userId,
                name: t("unknownUser") || "Unknown",
                count: userEventCount[userId],
              };
            }
          }),
        );

        const sorted = details
          .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
          .slice(0, MAX_USERS);

        if (!cancelled) setUserStats(sorted);
      } catch (err) {
        console.error("Error fetching user event participation:", err);
        if (!cancelled) {
          setError(
            t("errorFetchingParticipation") ||
              "Could not load participation data.",
          );
          setUserStats([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchEventAssignments();
    return () => {
      cancelled = true;
    };
  }, [organizationId, t]);

  const chartData = useMemo(() => {
    const colors = [
      "rgba(89, 50, 234, 0.55)",
      "rgba(124, 58, 237, 0.5)",
      "rgba(5, 150, 105, 0.5)",
      "rgba(219, 39, 119, 0.45)",
      "rgba(37, 99, 235, 0.5)",
      "rgba(8, 145, 178, 0.5)",
      "rgba(234, 88, 12, 0.45)",
      "rgba(79, 70, 229, 0.5)",
    ];
    return {
      labels: userStats.map((u) => u.name),
      datasets: [
        {
          label: t("eventsAssigned") || "Events assigned",
          data: userStats.map((u) => u.count),
          backgroundColor: userStats.map(
            (_, i) => colors[i % colors.length],
          ),
          borderColor: userStats.map((_, i) =>
            colors[i % colors.length].replace(/[\d.]+\)$/, "0.85)"),
          ),
          borderWidth: 1,
          borderRadius: 8,
          borderSkipped: false as const,
          maxBarThickness: 42,
        },
      ],
    };
  }, [userStats, t]);

  const isDark =
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark");

  if (loading) {
    return (
      <div className="p-5 sm:p-6 rounded-2xl border border-gray-200/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm animate-pulse">
        <div className="h-6 w-48 bg-gray-200 dark:bg-gray-600 rounded mb-4" />
        <div className="h-64 bg-gray-100 dark:bg-gray-700 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="p-5 sm:p-6 rounded-2xl border border-gray-200/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm">
      <div className="flex items-center gap-2.5 mb-5">
        <div className="flex items-center justify-center w-9 h-9 rounded-xl border border-[#5932EA]/25 bg-[#5932EA]/10 text-[#5932EA]">
          <HiOutlineUsers className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            {t("userEventParticipation") || "User event participation"}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t("userEventParticipationHint") ||
              "How many events each person is assigned to"}
          </p>
        </div>
      </div>

      {error ? (
        <p className="text-sm text-red-500 dark:text-red-400 py-8 text-center">
          {error}
        </p>
      ) : userStats.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center rounded-2xl border border-dashed border-gray-200 dark:border-gray-600">
          <HiOutlineUsers className="w-12 h-12 text-gray-400 mb-3 opacity-50" />
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
            {t("noParticipationYet") || "No participation data yet"}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-xs">
            {t("noParticipationYetHint") ||
              "Assign leaders or musicians to events to see this chart."}
          </p>
        </div>
      ) : (
        <div className="relative h-64 sm:h-72">
          <Bar
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  padding: 12,
                  cornerRadius: 8,
                  backgroundColor: isDark ? "#1f2937" : "#111827",
                  titleColor: "#f9fafb",
                  bodyColor: "#e5e7eb",
                },
              },
              scales: {
                x: {
                  grid: { display: false },
                  ticks: {
                    font: { size: 11 },
                    maxRotation: 40,
                    minRotation: 0,
                    color: isDark ? "#9ca3af" : "#6b7280",
                  },
                },
                y: {
                  beginAtZero: true,
                  ticks: {
                    stepSize: 1,
                    precision: 0,
                    font: { size: 11 },
                    color: isDark ? "#9ca3af" : "#6b7280",
                  },
                  grid: {
                    color: isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.06)",
                  },
                  border: { display: false },
                },
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

export default UserEventsChart;
